import { useLingui } from '@lingui/react/macro'
import { useEffect, useRef, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'

import { useAppSelector } from './hooks'
import { useGetSessionQuery, useRefreshSessionMutation } from './sessionApi'
import { selectSessionStatus } from './sessionSlice'

import { LoadingOranje } from '@/shared/components/LoadingOranje'

/**
 * Puerta de las rutas privadas. Vive en `app/` como el router: es cableado
 * global, no una feature.
 *
 * Al montar sin sesión intenta UNA VEZ `POST /auth/refresh` — si la cookie
 * `__session` vive, la sesión vuelve sin pedir login (así una recarga no
 * expulsa a nadie). Solo cuando el refresh falla se navega a `/login`.
 */
export function RequireSession(): ReactNode {
  const { t } = useLingui()
  const status = useAppSelector(selectSessionStatus)
  const location = useLocation()
  const [refreshSession] = useRefreshSessionMutation()
  const hasTriedRefresh = useRef(false)
  const { data: me, isError: meFailed } = useGetSessionQuery(undefined, {
    skip: status !== 'authenticated',
  })

  useEffect(() => {
    if (status !== 'unknown' || hasTriedRefresh.current) return
    hasTriedRefresh.current = true
    void refreshSession()
  }, [status, refreshSession])

  /* Los permisos llegan con `GET /me`. Sin esperarlos, cada pantalla se
     dibujaba una vez como si el usuario pudiera todo (`useCan` con la lista
     vacía): el Timesheet del hotel pedía datos de Contabilidad (403) y
     alcanzaba a pintar costes (Hugo, 2026-10-28). Si `/me` falla, sigue
     como antes. */
  if (status === 'authenticated') {
    if (me === undefined && !meFailed) return <LoadingOranje label={t`Abriendo tu sesión…`} />
    return <Outlet />
  }

  if (status === 'anonymous') {
    /** `from` permite volver a donde se iba después del login. */
    /* Quien venía a `/collaborator` vuelve por su propia puerta, con sus textos.
       También se reconoce la ruta vieja en español: los QR impresos y pegados
       en la puerta de un hotel siguen apuntando a `/colaborador/ponchar`. */
    const isWorkerRoute =
      location.pathname.startsWith('/collaborator') || location.pathname.startsWith('/colaborador')
    const login = isWorkerRoute ? '/collaborator/login' : '/login'
    /* Con la búsqueda: la liga del QR del acceso trae el código en `?qr=` y
       tiene que sobrevivir al login. */
    return <Navigate to={login} replace state={{ from: location.pathname + location.search }} />
  }

  /** `unknown` o `authenticating`: la naranja girando mientras el refresh decide. */
  return <LoadingOranje label={t`Abriendo tu sesión…`} />
}
